import React, { useState, useEffect } from 'react';
import { CheckCircle } from 'lucide-react';
import { getPageContent } from '../../services/pageService';
import Loader from '../../components/common/Loader';
import { usePageMeta } from '../../hooks/usePageMeta';

const DEFAULT_TEAM = [
  {
    name: 'Gurunatha Reddy Samala',
    role: 'Founder & Proprietor',
    image: 'https://media.licdn.com/dms/image/v2/D5603AQHB4fqRreGDkQ/profile-displayphoto-shrink_200_200/profile-displayphoto-shrink_200_200/0/1675257393166?e=2147483647&v=beta&t=leRZ_rabMpffRGTGsr5ApqkI5fk0rXxxxXw39u19dE8',
  },
  {
    name: 'Bhagyashree K R',
    role: 'Co-Founder',
    image: '/team/bhagyashree.jpeg',
  },
  {
    name: 'P Kumar',
    role: 'Receptionist',
    image: '/team/kumar.jpeg',
  },
];

const DEFAULT_WHY_CHOOSE_US = [
  'Experienced SAP-certified mentors',
  '100% practical training with real-time case studies',
  'Flexible learning – Online and Offline',
  'Personal mentorship and doubt clearing',
  'Placement support and interview preparation',
  'Affordable and flexible fee structure',
];

const AboutUs: React.FC = () => {
  const [content, setContent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const aboutContent = await getPageContent('about');
        setContent(aboutContent);
      } catch (err) {
        console.error('Error fetching about content:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchContent();
  }, []);

  const title = content?.title || 'About ShreeGuruCool';
  const subtitle = content?.subtitle || 'Empowering learners with SAP S/4HANA skills to transform careers and businesses.';

  usePageMeta({
    title,
    description: subtitle,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <Loader />
      </div>
    );
  }
  const whoWeAre = content?.whoWeAre || 'ShreeGuruCool is a dedicated SAP S/4HANA coaching institution committed to providing hands-on, real-world training in SD, MM, LE, and FICO modules. With a mission to make ERP education accessible, our programs blend practical experience with expert instruction — empowering both students and professionals for enterprise success.';
  const whyChooseUs = content?.whyChooseUs || DEFAULT_WHY_CHOOSE_US;
  const team = content?.team || DEFAULT_TEAM;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-blue-700 to-blue-900 text-white py-16 text-center">
        <h1 className="text-4xl font-bold mb-4">{title}</h1>
        <p className="text-lg max-w-2xl mx-auto text-blue-100 px-4">
          {subtitle}
        </p>
      </section>

      {/* Intro Section */}
      <section className="py-12 px-4 max-w-5xl mx-auto">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">Who We Are</h2>
        <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-line">
          {whoWeAre}
        </p>
      </section>

      {/* Why Choose Us */}
      <section className="bg-white py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-semibold mb-6 text-gray-800 text-center">Why Choose Us?</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700 text-lg">
            {whyChooseUs.map((item: string, idx: number) => (
              <li key={idx} className="flex items-start gap-2">
                <CheckCircle className="text-blue-600 mt-1 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Meet the Team */}
      <section className="py-12 px-4 bg-gray-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-semibold mb-8 text-center text-gray-800">Meet Our Team</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-center">
            {team.map((member: any) => (
              <div key={member.name} className="bg-white p-6 rounded-lg shadow">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-28 h-28 mx-auto rounded-full object-cover mb-4 border-4 border-blue-600"
                />
                <h3 className="text-xl font-bold text-gray-800">{member.name}</h3>
                <p className="text-gray-500">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
